import {Component, Mixins, Watch} from 'vue-property-decorator';
import LoadingMixin from "@/mixins/LoadingMixin";
import ProductMixin from '@/mixins/ProductMixin';
import ShoppingCartMixin from '@/mixins/ShoppingCartMixin';
import {
    ServiceAddress,
    CityState,
    Term,
    SelectedContractOption,
    CustomerWithContract, CustomerInfo
} from '@/products-and-services/ProductsAndServicesModels';
import {isEmpty} from "@/utils/string";
import {isEmailValid} from "@/utils/email";
import DropdownButton from '@/components/DropdownButton.vue';
import PhoneNumber from '@/components/PhoneNumber.vue';
import { getTerms } from '@/products-and-services/ProductsAndServicesService';

@Component({
    components: {
        DropdownButton,
        PhoneNumber
    }
})
export default class NewCustomer extends Mixins(LoadingMixin, ProductMixin, ShoppingCartMixin) {
    private firstName: string = '';
    private lastName: string = '';
    private email: string = '';
    private productsAndPromotionsOption: boolean = false;
    private landLinePhoneNumber: string = '';
    private mobilePhoneNumber: string = '';
    private mailSameAsService: boolean = false;
    private mailStreetAddress: string = '';
    private mailUnit: string = '';
    private mailCity: string = '';
    private mailState: string = 'ND';
    private mailZipCode: string = '';
    private paymentOption: string = '';
    private installationNotes: string = '';
    private errorMessage: string = '';

    private firstNameError: boolean = false;
    private lastNameError: boolean = false;
    private emailError: boolean = false;
    private phoneNumberError: boolean = false;
    private shouldShowPhoneError: boolean = false;
    private mailStreetAddressError: boolean = false;
    private mailCityError: boolean = false;
    private mailStateError: boolean = false;
    private mailZipCodeError: boolean = false;
    private paymentOptionError: boolean = false;
    private terms: Term[] = [];

    get isCurrentPageNewCustomer () {
        return this.$route.meta && this.$route.meta.onNewCustomer
    }

    get cartNumber(): string|number {
        return this.$store.getters.getCartNumber();
    }

    get location(): ServiceAddress {
        const location = this.$store.getters.getLocation as ServiceAddress;
        return location ? location : {} as ServiceAddress;
    }

    get statesAbbrev(): CityState[] {
        const states = this.$store.state.states;
        if (states && states.length) {
            return states.map((state: CityState) => state.stateAbbreviation);
        }
        return [];
    }

    get reviewLabel(): string {
        return this.isEmployee ? 'Update and Return' : 'Review Order';
    }

    get isResidential(): boolean {
        return window.location.pathname.includes('residential');
    }

    get contactName1(): string {
        if (this.isResidential) {
            return 'First Name'
        } else {
            return 'Business Name'
        }
    }

    get contactName2(): string {
        if (this.isResidential) {
            return 'Last Name'
        } else {
            return 'Contact Name'
        }
    }



    @Watch('$route.name')
    public routeChanged() {
        this.setCustomer();
    }

    @Watch('isLoading')
    public isLoadingChanged() {
        this.setCustomer();
    }

    public async created() {
        this.setCustomer();

        this.terms = await getTerms(this.isResidential);
    }

    private setCustomer() {
        if (this.isLoading) {
            return;
        }
        if(this.$store.state.emailForPromotions){
            this.email = this.$store.state.emailForPromotions;
            this.productsAndPromotionsOption = true;
        }
        const customer = this.$store.getters.getCustomerInfo as CustomerInfo | undefined;

        if (customer) {
            this.firstName = customer.firstName;
            this.lastName = customer.lastName;
            this.email = customer.email;
            this.productsAndPromotionsOption = customer.productsAndPromotionsOption;
            this.landLinePhoneNumber = customer.landLinePhoneNumber;
            this.mobilePhoneNumber = customer.mobilePhoneNumber;
            this.mailSameAsService = customer.mailSameAsService;
            this.mailStreetAddress = customer.mailAddress;
            this.mailUnit = customer.mailUnit;
            this.mailCity = this.mailSameAsService ? this.location.serviceCityDisplayName! : customer.mailCity;
            this.mailState = customer.mailState;
            this.mailZipCode = customer.mailZipCode;
            this.paymentOption = this.$store.state.shoppingCartData.selectedContractId;
            this.installationNotes = customer.installationNotes;
        }
    }

    isChecked(value: string){
        return this.paymentOption === value;
    }

    copyServiceAddress(): void {
        if(this.mailSameAsService){
            this.mailStreetAddress = this.location.serviceAddress;
            this.mailUnit = this.location.serviceUnit;
            this.mailCity = this.location.serviceCityDisplayName!;
            this.mailState = this.location.serviceState;
            this.mailZipCode = this.location.serviceZipCode;
            this.checkFields();
        }
    }

    mailStateSelected(value: string) {
        this.mailState = value;
    }

    validateEmail() {
        if (!isEmailValid(this.email)) {
            this.emailError = true;
            this.errorMessage = "The email provided is invalid."
        } else {
            this.emailError = false;
            this.errorMessage = "";
        }
    }

    updatePaymentOption(value: string) {
        this.paymentOption = value;
        this.checkFields();
    }

    checkFields(): CustomerWithContract | null {
        this.firstNameError = isEmpty(this.firstName);
        this.lastNameError = isEmpty(this.lastName);
        this.phoneNumberError = isEmpty(this.landLinePhoneNumber) && isEmpty(this.mobilePhoneNumber);
        if (!this.phoneNumberError) {
            if (!isEmpty(this.landLinePhoneNumber) && this.landLinePhoneNumber.length < 12) {
                this.phoneNumberError = true;
            } else if (!isEmpty(this.mobilePhoneNumber) && this.mobilePhoneNumber.length < 12) {
                this.phoneNumberError = true;
            }
        }
        this.mailStreetAddressError = isEmpty(this.mailStreetAddress);
        this.mailCityError = isEmpty(this.mailCity);
        this.mailStateError = isEmpty(this.mailState);
        this.mailZipCodeError = isEmpty(this.mailZipCode);
        this.paymentOptionError = isEmpty(this.paymentOption);
        this.emailError = !isEmailValid(this.email);
        this.shouldShowPhoneError = false;
        if(this.pageHasErrors()) {
            this.errorMessage = "Please fill all required fields.";
        } else if (this.phoneNumberError) {
            this.shouldShowPhoneError = true;
            this.errorMessage = "At least one phone number is required and must be 10 digits.";
        } else if (this.paymentOptionError) {
            this.errorMessage = "Please select an agreement.";
        } else if (this.emailError) {
            this.errorMessage = "The email provided is invalid.";
        } else {
            this.errorMessage = "";
            return {
                mobilePhoneNumber: this.mobilePhoneNumber,
                email: this.email.toUpperCase(),
                productsAndPromotionsOption: this.productsAndPromotionsOption,
                firstName: this.firstName.toUpperCase(),
                installationNotes: this.installationNotes,
                lastName: this.lastName.toUpperCase(),
                mailAddress: this.mailStreetAddress,
                mailCity: this.mailSameAsService ? this.location.serviceCity : this.mailCity,
                mailState: this.mailState,
                mailUnit: this.mailUnit,
                mailZipCode: this.mailZipCode,
                landLinePhoneNumber: this.landLinePhoneNumber,
                mailSameAsService: this.mailSameAsService,
                selectedContractId: this.paymentOption
            };
        }
        return null;
    }

    pageHasErrors(): boolean {
        return (
            this.firstNameError ||
            this.lastNameError ||
            this.mailStreetAddressError ||
            this.mailCityError ||
            this.mailStateError ||
            this.mailZipCodeError
        );
    }

    async submitInfo(): Promise<any> {
        const customer = this.checkFields();
        if(customer !== null) {
            await this.execute(async () => {
                //this should probably happen at the way end after capcha so it's not abusable
                await this.$store.dispatch('updateOrderDetails', { mutation: 'setCustomer', item: customer });
                if (this.isEmployee) {
                    this.returnToEmployeePortal();
                } else {
                    await this.$router.push({path: '/new-customer/order-review'});
                }
            }, 'Failed to submit customer data');
        }
    }
}
